import { Box, Heading, Select, Stack, useRadioGroup } from '@chakra-ui/react';

import { collection, doc, getDocs, query, where } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import RadioCard from '~/components/Radio/RadioCard';
import firebaseFirestore from '~/config/firebase/firestore/firestore';
import { Location } from '~/core/types';
import { useAuth } from '~/hooks/useAuth';

type LocationsProps = {
  companyUid?: string;
  onChange: (locationUid: string) => void;
  isVisibleAll?: boolean;
  selectFirst?: boolean;
};

const Locations = ({
  companyUid = '',
  onChange,
  isVisibleAll = true,
  selectFirst = false,
}: LocationsProps) => {
  const [locations, setLocations] = useState<Location[]>([]);
  const { user } = useAuth();

  const { getRootProps, getRadioProps, setValue, value } = useRadioGroup({
    name: 'framework',
    onChange: onChange,
  });

  const group = getRootProps();

  /**
   * Solo las sedes abiertas a reservas.
   *
   * Una sede desactivada desde el superadministrador desaparece del panel, pero
   * sus reservas siguen guardadas y los informes las siguen contando: los
   * informes leen la colección de reservas, no esta.
   */
  const getLocations = async () => {
    const companyRef = doc(firebaseFirestore, 'companies', companyUid);

    const locationsRef = collection(firebaseFirestore, 'locations');
    const q = query(
      locationsRef,
      where('company', '==', companyRef),
      where('status', '==', 'active')
    );

    getDocs(q).then((querySnapshot) => {
      const mappedLocations: Partial<Location>[] = querySnapshot.docs.map((doc) => {
        return {
          id: doc.id,
          ...doc.data(),
        };
      });
      if (mappedLocations.length > 0 && isVisibleAll) {
        mappedLocations.unshift({ id: '', name: 'Todas' });
        // Deja "Todas" marcada al entrar, en lugar de no marcar ninguna.
        setValue('');
      }
      setLocations(mappedLocations as Location[]);
    });
  };

  useEffect(() => {
    if (user.role === 'admin') {
      getLocations();
      return;
    }
    if (user.role === 'location-manager') {
      // Las sedes asignadas se guardan al iniciar sesión, así que se comprueba
      // contra la base cuáles siguen abiertas: si no, una sede desactivada hoy
      // le seguiría apareciendo hasta que volviera a entrar.
      const asignadas: Partial<Location>[] = [...(user.locations ?? [])];
      const empresa = companyUid || user.company?.id;
      if (empresa && asignadas.length > 0) {
        getDocs(
          query(
            collection(firebaseFirestore, 'locations'),
            where('company', '==', doc(firebaseFirestore, 'companies', empresa)),
            where('status', '==', 'active')
          )
        )
          .then((snap) => {
            const abiertas = new Set(snap.docs.map((d) => d.id));
            mostrarSedes(asignadas.filter((l) => abiertas.has(l.id ?? '')));
          })
          // Si la consulta falla, se usa lo que se guardó al entrar antes que
          // dejar la pantalla vacía.
          .catch(() => mostrarSedes(asignadas.filter((l) => (l as any).status !== 'inactive')));
        return;
      }

      mostrarSedes(asignadas.filter((l) => (l as any).status !== 'inactive'));
      return;
    }
  }, [user]);

  /** Pinta la lista del gestor de sede y deja marcada la primera opción. */
  function mostrarSedes(lista: Partial<Location>[]) {
    const userLocations = [...lista];

    // Sin ubicaciones asignadas no hay nada que mostrar, pero la pantalla
    // no debe romperse: antes se leía locations[0] y lanzaba una excepción.
    if (userLocations.length === 0) {
      setLocations([]);
      onChange('');
      return;
    }

    // "Todas" solo tiene sentido donde se listan datos de varias sedes.
    if (isVisibleAll && userLocations.length > 1) {
      userLocations.unshift({ id: '', name: 'Todas' });
    }

    setLocations(userLocations as Location[]);

    // Deja la primera opción marcada de verdad: con varias sedes será
    // "Todas", así el usuario entra viendo toda su información.
    const initialId = userLocations[0].id ?? '';
    setValue(initialId);
    onChange(initialId);
  }

  useEffect(() => {
    if (selectFirst) {
      setValue(locations[0]?.id ?? '');
      onChange(locations[0]?.id ?? '');
    }
  }, [selectFirst, locations]);

  return (
    <Box>
      <Heading fontSize={'large'}>Ubicaciones</Heading>

      <Stack
        flexDirection={{ sm: 'row', md: 'column' }}
        mt={2}
        {...group}
        spacing={{
          sm: 0,
          md: 2,
        }}
        gap={{
          sm: 2,
          md: 2,
        }}
        alignItems={{
          sm: 'center',
          md: 'flex-start',
        }}
      >
        {locations.length > 6 && (
          // Con muchas sedes se muestra un desplegable en lugar de botones.
          // Va controlado por el mismo valor que los botones: si no, se
          // quedaba en "Seleccione…" aunque hubiera una sede seleccionada.
          <Select
            w={{ sm: '100%', md: 'auto' }}
            value={String(value ?? '')}
            placeholder={isVisibleAll ? undefined : 'Seleccione una ubicación'}
            onChange={(e) => {
              setValue(e.target.value);
              onChange(e.target.value);
            }}
          >
            {locations.map((location) => {
              return (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              );
            })}
          </Select>
        )}
        {locations.length <= 6 &&
          locations.map((value) => {
            const radio = getRadioProps({ value: value.id });
            return (
              <RadioCard key={value.id} {...radio}>
                {value.name}
              </RadioCard>
            );
          })}
      </Stack>
    </Box>
  );
};

export default Locations;
